import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Card,
  Chip,
  ListSubheader,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useCustomerGroups,
  usePriceLinesForProducts,
  usePriceLists,
  useProducts,
  useSearchProducts,
  useSalesChannels,
} from '../../hooks';
import { formatPrice } from '../../lib/format';
import {
  BASE_SCOPE,
  decodeScope,
  deriveTierColumns,
  effectiveTierAmount,
  encodeScope,
  resolveGridCell,
  resolveListGridCell,
} from '../../lib/priceGrid';
import type { GridScope, MatchedRung } from '../../lib/priceGrid';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import type { ToastSeverity } from '../../lib/useToast';
import type { CustomerGroupResponse } from '../../model/customerGroupTypes';
import type { PriceListResponse } from '../../model/priceListTypes';
import type { PriceLineResponse, Product, SalesChannelResponse } from '../../model/types';
import type { PriceCellEditorContext } from './PriceCellEditorDrawer';
import { PriceCellEditorDrawer } from './PriceCellEditorDrawer';

// ── Constants ─────────────────────────────────────────────────────────────────

/** Non-empty sentinel — MUI Select treats '' as its own "nothing selected" state, so a real
 * "all customers / no group" option needs a distinct value (never ''). */
const ALL_CUSTOMERS_VALUE = '__all-customers__';

// ── Helpers ───────────────────────────────────────────────────────────────────

type ChipColor = 'success' | 'warning' | 'error' | 'default';

function stateColor(state: string): ChipColor {
  if (state === 'ACTIVE') return 'success';
  if (state === 'REQUIRES_APPROVAL') return 'warning';
  if (state === 'BLOCKED') return 'error';
  return 'default';
}

interface ScopeResolution {
  line: PriceLineResponse;
  inherited: boolean;
  mirroredFromChannelCode?: string;
  matchedRung?: MatchedRung;
}

/** Unifies base/channel(+group) resolution (`resolveGridCell` — mirror-effective-channel-first per
 * adr-pricing-005, composed with the optional customer-group axis per adr-pricing-009) with list
 * resolution (`resolveListGridCell` — no inheritance/mirroring/group at all, per design spec §8.5)
 * into one shape the row renderer below can stay agnostic about. */
function resolveForScope(
  lines: PriceLineResponse[],
  scope: GridScope,
  now: Date,
  channels: SalesChannelResponse[],
  customerGroupCode: string | null,
): ScopeResolution | null {
  if (scope.kind === 'list') {
    const line = resolveListGridCell(lines, scope.code, now);
    return line ? { line, inherited: false } : null;
  }
  const channelCode = scope.kind === 'channel' ? scope.code : null;
  return resolveGridCell(lines, channelCode, now, channels, customerGroupCode);
}

/** The inherited-prefix caption for a resolved cell — mirror provenance first, then the WP4.3
 * rung-name labels (only ever populated when a customer group is part of the query), falling back
 * to the original generic "= base" label so pre-group behaviour is byte-identical. */
function inheritedPrefixKey(resolution: ScopeResolution): string {
  if (resolution.mirroredFromChannelCode) return 'priceGridPanel.mirroredPrefix';
  if (resolution.matchedRung === 'channel') return 'priceGridPanel.channelPrefix';
  if (resolution.matchedRung === 'group') return 'priceGridPanel.groupPrefix';
  return 'priceGridPanel.inheritedPrefix';
}

// ── Sub-component: PriceGridRow ───────────────────────────────────────────────

interface PriceGridRowProps {
  product: Product;
  scope: GridScope;
  channels: SalesChannelResponse[];
  customerGroupCode: string | null;
  tiers: number[];
  lines: PriceLineResponse[];
  canPricingManage: boolean;
  onCellClick: (product: { id: string; name?: string }, lines: PriceLineResponse[]) => void;
}

function PriceGridRow({
  product,
  scope,
  channels,
  customerGroupCode,
  tiers,
  lines,
  canPricingManage,
  onCellClick,
}: PriceGridRowProps) {
  const { t } = useTranslation('catalog');
  const now = new Date();
  const resolution = resolveForScope(lines, scope, now, channels, customerGroupCode);
  const isListScope = scope.kind === 'list';

  const handleCellClick = () => {
    if (canPricingManage) {
      onCellClick({ id: product.id, name: product.name }, lines);
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {product.name}
        </Typography>
      </TableCell>

      {tiers.map((tier) => {
        const amount = resolution ? effectiveTierAmount(resolution.line, tier) : undefined;
        return (
          <TableCell
            key={tier}
            align="right"
            onClick={handleCellClick}
            role={canPricingManage ? 'button' : undefined}
            tabIndex={canPricingManage ? 0 : undefined}
            onKeyDown={
              canPricingManage
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCellClick();
                    }
                  }
                : undefined
            }
            sx={{
              cursor: canPricingManage ? 'pointer' : 'default',
              '&:focus-visible': canPricingManage
                ? {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                  }
                : undefined,
            }}
          >
            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
              {resolution?.inherited ? (
                <Typography component="span" variant="caption" color="text.disabled">
                  {resolution.mirroredFromChannelCode
                    ? t(inheritedPrefixKey(resolution), { channel: resolution.mirroredFromChannelCode })
                    : t(inheritedPrefixKey(resolution))}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {amount !== undefined ? formatPrice(amount) : '—'}
              </Typography>
              {!resolution && isListScope ? (
                <Tooltip title={t('priceGridPanel.listFallthroughTooltip')}>
                  <Typography component="span" variant="caption" color="text.disabled">
                    {t('priceGridPanel.listFallthrough')}
                  </Typography>
                </Tooltip>
              ) : null}
            </Stack>
          </TableCell>
        );
      })}

      <TableCell>
        {resolution ? (
          <Chip size="small" label={resolution.line.state} color={stateColor(resolution.line.state)} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Drawer state ──────────────────────────────────────────────────────────────

interface DrawerState {
  product: { id: string; name?: string };
  context: PriceCellEditorContext;
  lines: PriceLineResponse[];
}

// ── Main export ───────────────────────────────────────────────────────────────

export interface PriceGridPanelProps {
  /** The "Prices for:" scope (design spec §8.5) — owned by `PricingPage` and mirrored to
   * `?scope=` (design spec §5/§12), the same controlled-from-the-page pattern as
   * `PriceListsPanel`'s/`BulkChangesPanel`'s own `view`/`onViewChange`. */
  scope: GridScope;
  onScopeChange: (scope: GridScope) => void;
  /**
   * Seeds the customer-group select from `?group=` (design spec §12.4, plan-merchant-portal-018
   * WP1.4) — the drawer's "Other prices" row already links here with `&group=CODE` for a
   * group/channel+group slot. Unlike `scope`, the group axis stays this panel's OWN local state
   * (`PricingPage` only feeds the initial value, mirroring `PriceListsPanel`'s `seedStore`) —
   * read once, at mount; a later prop change (e.g. a second deep link without an intervening
   * unmount) is intentionally NOT tracked, the same limitation `seedStore`/`initialTargetListCode`
   * already accept.
   */
  initialGroupCode?: string | null;
}

export function PriceGridPanel({ scope, onScopeChange, initialGroupCode }: PriceGridPanelProps) {
  const { t } = useTranslation('catalog');
  const [query, setQuery] = useState('');
  const [customerGroupValue, setCustomerGroupValue] = useState<string>(
    () => initialGroupCode ?? ALL_CUSTOMERS_VALUE,
  );
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);

  const { canPricingView, canPricingManage } = useCatalogPermissions();
  const { toast, notify, clear } = useToast();

  const {
    data: channelsResult,
    isLoading: channelsLoading,
    isError: channelsError,
    refetch: refetchChannels,
  } = useSalesChannels();
  const {
    data: listsResult,
    isLoading: listsLoading,
    isError: listsError,
  } = usePriceLists();
  // Additive/optional (design spec §8.10) — a groups-fetch failure never blocks the grid; the
  // group axis simply stays hidden, degrading to today's no-group behaviour.
  const { data: groupsResult } = useCustomerGroups();

  const listQ = useProducts({ pagination: { page, size } }, { enabled: !query });
  const searchQ = useSearchProducts({ query, pagination: { page, size } }, { enabled: Boolean(query) });

  const productsLoading = query ? searchQ.isLoading : listQ.isLoading;
  const productsError = query ? searchQ.isError : listQ.isError;

  // ── Data (computed before any early return — the batched lines fetch right below is a hook
  // call and every hook in this component must run unconditionally, every render) ─────────────

  const channels = channelsResult ?? [];
  const lists = listsResult ?? [];
  const groups: CustomerGroupResponse[] = groupsResult ?? [];
  const productsPage = query ? searchQ.data : listQ.data;
  const products = productsPage?.content ?? [];
  const productsTotal = productsPage?.totalElements ?? 0;

  // A list deactivated (or removed) while it was the selected scope falls back to Base — a list's
  // ladder rung is skipped when inactive (adr-pricing-025 d4), so showing its grid as if it still
  // resolved would be misleading.
  const effectiveScope: GridScope =
    scope.kind === 'list' && !lists.some((l) => l.code === scope.code && l.active) ? { kind: 'base' } : scope;

  // Structural exclusivity (design spec §8.5/§8.10) — a list slot never composes with a customer
  // group, so the axis is disabled (not cleared) in list scope; its own selection is preserved for
  // when the merchant switches back to base/channel scope.
  const groupAxisDisabled = effectiveScope.kind === 'list';
  const effectiveGroupCode =
    !groupAxisDisabled && customerGroupValue !== ALL_CUSTOMERS_VALUE ? customerGroupValue : null;

  // ── Batched per-page price-line fetch (R5 fix, WP4.3) — one usePriceLinesForProducts
  // composition (itself a single hook call wrapping `useQueries`, safe to feed a
  // render-to-render-varying id list — the same pattern `usePriceListsOverview` already uses) for
  // the whole visible page instead of each row owning its own query. Must run unconditionally
  // (Rules of Hooks), so it sits here, before the guards below. ──────────────────────────────
  const productIds = products.map((p) => p.id);
  const { linesByProductId } = usePriceLinesForProducts(productIds);

  // ── Permission guard ──────────────────────────────────────────────────────

  if (!canPricingView) {
    return (
      <ErrorState
        title={t('pricing.accessRestricted.title')}
        message={t('pricing.accessRestricted.message')}
      />
    );
  }

  // ── Async guards ──────────────────────────────────────────────────────────

  if (channelsLoading || listsLoading || productsLoading) {
    return <LoadingState label={t('priceGridPanel.loading')} />;
  }

  if (channelsError || listsError || productsError) {
    return (
      <ErrorState
        message={t('priceGridPanel.loadError')}
        onRetry={() => {
          void refetchChannels();
          void (query ? searchQ.refetch() : listQ.refetch());
        }}
      />
    );
  }

  const resolvedLinesForTiers = products
    .map((p) => {
      const lines = linesByProductId.get(p.id) ?? [];
      return resolveForScope(lines, effectiveScope, new Date(), channels, effectiveGroupCode)?.line;
    })
    .filter((line): line is PriceLineResponse => Boolean(line));
  const tiers = deriveTierColumns(resolvedLinesForTiers);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCellClick = (product: { id: string; name?: string }, lines: PriceLineResponse[]) => {
    const context = buildContext(effectiveScope, channels, lists, effectiveGroupCode, groups);
    setDrawerState({ product, context, lines });
  };

  const handleDrawerResult = (severity: ToastSeverity, message: string): void => {
    notify(severity, message);
  };

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setPage(0);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          label={t('priceGridPanel.searchLabel')}
          value={query}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label={t('priceGridPanel.scopeLabel')}
          value={encodeScope(effectiveScope)}
          onChange={(e) => onScopeChange(decodeScope(e.target.value))}
          sx={{ minWidth: 220 }}
          slotProps={{ select: { 'aria-label': t('priceGridPanel.scopeLabel') } }}
        >
          <MenuItem value={BASE_SCOPE}>{t('priceGridPanel.baseChannel')}</MenuItem>
          <ListSubheader>{t('priceGridPanel.scopeGroups.channels')}</ListSubheader>
          {channels
            .filter((c) => c.active)
            .map((c) => (
              <MenuItem key={c.code} value={encodeScope({ kind: 'channel', code: c.code })}>
                {c.name}
              </MenuItem>
            ))}
          <ListSubheader>{t('priceGridPanel.scopeGroups.lists')}</ListSubheader>
          {lists.length === 0 ? (
            <MenuItem value="__no-lists__" disabled>
              {t('priceGridPanel.scopeNoLists')}
            </MenuItem>
          ) : (
            lists.map((list) => (
              <MenuItem
                key={list.code}
                value={encodeScope({ kind: 'list', code: list.code })}
                disabled={!list.active}
              >
                {list.name}
                {!list.active ? ` (${t('priceGridPanel.scopeListInactiveSuffix')})` : ''}
              </MenuItem>
            ))
          )}
        </TextField>

        {/* Customer-group axis (design spec §8.10) — invisible with zero footprint for tenants
            with no groups; disabled (not cleared) while the list scope is selected. */}
        {groups.length > 0 ? (
          <Tooltip
            title={groupAxisDisabled ? t('priceGridPanel.groupListExclusive') : ''}
            disableHoverListener={!groupAxisDisabled}
          >
            <TextField
              select
              size="small"
              label={t('priceGridPanel.groupLabel')}
              value={customerGroupValue}
              disabled={groupAxisDisabled}
              onChange={(e) => setCustomerGroupValue(e.target.value)}
              sx={{ minWidth: 200 }}
              slotProps={{ select: { 'aria-label': t('priceGridPanel.groupLabel') } }}
            >
              <MenuItem value={ALL_CUSTOMERS_VALUE}>{t('priceGridPanel.groupAll')}</MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.code} value={group.code} disabled={!group.active}>
                  {group.name}
                  {!group.active ? ` (${t('priceGridPanel.groupInactiveSuffix')})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Tooltip>
        ) : null}
      </Stack>

      {/* Table or empty */}
      {products.length === 0 ? (
        <EmptyState
          title={t('priceGridPanel.empty.title')}
          description={t('priceGridPanel.empty.description')}
        />
      ) : (
        <>
          <TableContainer component={Card} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('priceGridPanel.table.product')}</TableCell>
                  {tiers.map((tierQty) => (
                    <TableCell key={tierQty} align="right">
                      {t('priceGridPanel.table.tierFrom', { qty: tierQty })}
                    </TableCell>
                  ))}
                  <TableCell>{t('priceGridPanel.table.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <PriceGridRow
                    key={product.id}
                    product={product}
                    scope={effectiveScope}
                    channels={channels}
                    customerGroupCode={effectiveGroupCode}
                    tiers={tiers}
                    lines={linesByProductId.get(product.id) ?? []}
                    canPricingManage={canPricingManage}
                    onCellClick={handleCellClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CompactTablePagination
            count={productsTotal}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      {/* Editor drawer */}
      <PriceCellEditorDrawer
        open={drawerState !== null}
        product={drawerState?.product ?? { id: '' }}
        context={drawerState?.context ?? { type: 'base' }}
        existingLines={drawerState?.lines ?? []}
        onClose={() => setDrawerState(null)}
        onResult={handleDrawerResult}
      />

      {/* Toast */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function buildContext(
  scope: GridScope,
  channels: SalesChannelResponse[],
  lists: PriceListResponse[],
  customerGroupCode: string | null,
  groups: CustomerGroupResponse[],
): PriceCellEditorContext {
  if (scope.kind === 'list') {
    const list = lists.find((l) => l.code === scope.code);
    return { type: 'list', code: scope.code, name: list?.name ?? scope.code };
  }
  const group = customerGroupCode
    ? { code: customerGroupCode, name: groups.find((g) => g.code === customerGroupCode)?.name }
    : undefined;
  if (scope.kind === 'channel') {
    const channel = channels.find((c) => c.code === scope.code);
    return { type: 'channel', code: scope.code, name: channel?.name, group };
  }
  return { type: 'base', group };
}
